import Image from "@tiptap/extension-image";
import { Editor, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { errorNotificationVar } from "@cache/cache";
import useDebounce from "@components/use-debounce/use-debounce";
import client from "@graphql/client";
import { classNames } from "@helpers/css";

import prepareTopicFileUploadMutation from "../graphql/prepare-topic-file-upload-mutation";
import { uploadImagePlugin } from "./image-upload";

/**
 * Tiptap Extension to upload images
 * @see  https://gist.github.com/slava-vishnyakov/16076dff1a77ddaca93c4bccd4ec4521#gistcomment-3744392
 * @since 7th July 2021
 *
 * https://github.com/ueberdosis/tiptap/blob/main/packages/extension-image/src/image.ts
 *
 * Matches following attributes in Markdown-typed image: [, alt, src, title]
 *
 * Example:
 * ![Lorem](image.jpg) -> [, "Lorem", "image.jpg"]
 * ![](image.jpg "Ipsum") -> [, "", "image.jpg", "Ipsum"]
 * ![Lorem](image.jpg "Ipsum") -> [, "Lorem", "image.jpg", "Ipsum"]
 */

const minWidthImage = 50;
const maxWidthImage = 1000;

const ImageComponent = memo(
  ({
    node,
    updateAttributes,
    selected,
    deleteNode,
    editor,
  }: {
    node: any;
    updateAttributes: (attributes: any) => void;
    selected: boolean;
    deleteNode: () => void;
    editor: Editor;
  }) => {
    const ref = useRef(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const attrWidth = node.attrs.width;
    const debouncedAttrWidth = useDebounce(attrWidth, 500);
    const widthRef = useRef<number | string | undefined>(attrWidth);
    const [width, setWidth] = useState<number | string | undefined>(attrWidth);

    const handleMouseUp = useCallback(() => {
      const validWidth = widthRef.current
        ? parseInt(String(widthRef.current))
        : undefined;
      if (validWidth) {
        updateAttributes({ width: validWidth });
      }
    }, [updateAttributes]);

    const imgRef = useRef(null);

    const startResize = (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const parentWidth = parentRef.current?.clientWidth || false;
      if (!parentWidth) return;
      const startX = e.clientX;
      const startWidth = widthRef.current
        ? parseInt(String(widthRef.current))
        : parentWidth;

      const onMouseMove = (ev: any) => {
        const newWidth = startWidth + (ev.clientX - startX);
        const validWidth = Math.min(
          parentWidth || maxWidthImage,
          Math.max(minWidthImage, newWidth)
        );
        setWidth(validWidth || undefined);
        widthRef.current = validWidth;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        handleMouseUp();
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    useEffect(() => {
      if (debouncedAttrWidth) {
        setWidth(debouncedAttrWidth);
      }
    }, [debouncedAttrWidth]);

    return (
      <NodeViewWrapper className="react-component not-prose" ref={parentRef}>
        <div
          className={classNames(
            "relative flex group ",
            selected && "ring-2 ring-blue-500"
          )}
          ref={ref}
          style={{ maxWidth: width }}
        >
          <img
            ref={imgRef}
            src={node.attrs.src}
            alt="Uploaded by user"
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
            }}
          />
          <div
            onMouseDown={startResize}
            style={{
              cursor: "se-resize",
            }}
            className={classNames(
              "absolute h-[10px] w-[10px] -right-[6px] -bottom-[6px] bg-blue-500 hidden",
              selected && "block"
            )}
          />
        </div>
      </NodeViewWrapper>
    );
  }
);

export const asyncUploadImageErrorMessage = "The image could not be uploaded.";

export type WysiwygUploadVariable = {
  topicId?: number;
  artifactId?: number;
  recognitionCoreValueId?: number;
  organizationId?: number;
};

export const asyncUploadImage = (uploadVariable: WysiwygUploadVariable) => {
  return (file: File) => {
    const p: Promise<string | undefined> = new Promise((resolve, reject) => {
      // we upload the image to S3 with presigned url
      // https://docs.aws.amazon.com/AmazonS3/latest/userguide/PresignedUrlUploadObject.html
      client
        .mutate({
          mutation: prepareTopicFileUploadMutation,
          variables: {
            filename: `${Date.now()}${file.name.substring(0, 100)}`,
            contentType: file.type,
            ...uploadVariable,
          },
        })
        .then(({ data }) => {
          const { fileUploadUrl, fileViewingUrl } = data.prepareFileUpload;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = function (event: ProgressEvent<FileReader>) {
            if (event.target?.readyState !== FileReader.DONE) {
              return;
            }
            fetch(fileUploadUrl, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file,
            }).then(() => {
              resolve(fileViewingUrl);
            });
          };
        })
        .catch((e) => {
          errorNotificationVar({
            title: "The image could not be uploaded.",
            description: e.message,
          });
          reject(asyncUploadImageErrorMessage);
        });
    });
    return p;
  };
};

export interface TipTapCustomImageOptions {
  width?: number;
}

export const TipTapCustomImage = (
  uploadFn: undefined | ((file: File) => Promise<string | undefined>)
) => {
  return Image.extend<TipTapCustomImageOptions>({
    addAttributes() {
      return {
        src: {
          default: null,
        },
        width: {
          default: "",
          parseHTML: () => this.options.width,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: "img",
        },
      ];
    },
    addNodeView() {
      return ReactNodeViewRenderer(ImageComponent);
    },
    addProseMirrorPlugins() {
      return uploadFn ? [uploadImagePlugin(uploadFn)] : [];
    },
  });
};

export default TipTapCustomImage;
